import { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, FileText, CreditCard, ArrowLeft, CheckCircle, Sparkles, AlertCircle, Tag } from 'lucide-react'
import { paymentsApi, authApi, CreateOrderResponse, SelectPlanResponse, ValidateDiscountResponse } from '../services/api'
import { formatDateDDMMYYYY } from '../utils/date'

const EXTERNAL_PRICING_URL = 'https://www.lextool.pl/cennik'

const PLAN_LABELS: Record<string, string> = {
  basic: 'Pakiet Solo',
  extended: 'Pakiet Kancelaria',
  advanced: 'Pakiet Enterprise',
}

export default function PricingPage() {
  const [searchParams] = useSearchParams()
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [orderData, setOrderData] = useState<CreateOrderResponse | null>(null)
  const [trialData, setTrialData] = useState<SelectPlanResponse | null>(null)
  const [conflictStatus, setConflictStatus] = useState<string | null>(null)
  const [discountCode, setDiscountCode] = useState('')
  const [discountResult, setDiscountResult] = useState<ValidateDiscountResponse | null>(null)
  const [discountError, setDiscountError] = useState<string | null>(null)
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false)

  const planId = searchParams.get('plan')
  const period = (searchParams.get('period') as 'monthly' | 'yearly') || 'yearly'
  const isTrial = searchParams.get('trial') === 'true'

  const createOrderMutation = useMutation({
    mutationFn: (data: { planId: string; billingPeriod: 'monthly' | 'yearly'; discountCode?: string }) =>
      paymentsApi.createOrder(data),
    onSuccess: (response) => {
      setOrderData(response.data)
    },
    onError: () => {
      setError('Nie udało się utworzyć zamówienia. Spróbuj ponownie.')
    },
  })

  const selectPlanMutation = useMutation({
    mutationFn: (data: { planId: string; startTrial: boolean }) =>
      authApi.selectPlan(data),
    onSuccess: (response) => {
      if (response.data.status === 'trial_started') {
        setTrialData(response.data)
        queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
        queryClient.invalidateQueries({ queryKey: ['subscription-current'] })
      }
    },
    onError: (err: Error & { response?: { status?: number; data?: SelectPlanResponse } }) => {
      if (err.response?.status === 409 && err.response.data) {
        setConflictStatus(err.response.data.status || 'unknown')
        setTrialData(err.response.data)
      } else {
        setError('Nie udało się aktywować okresu próbnego. Spróbuj ponownie.')
      }
    },
  })

  const mutationFiredRef = useRef(false)

  // Reset mutation fired flag when parameters change so we can refire the correct mutation
  useEffect(() => {
    mutationFiredRef.current = false
  }, [planId, period, isTrial])

  // No plan selected -> redirect to external pricing page
  useEffect(() => {
    if (!planId) {
      window.location.href = EXTERNAL_PRICING_URL
    }
  }, [planId])

  // Wait for auth to load before deciding
  useEffect(() => {
    if (!planId || authLoading) return

    if (!isAuthenticated) {
      const trialParam = isTrial ? '&trial=true' : ''
      navigate(`/register?plan=${planId}&period=${period}${trialParam}`, { replace: true })
    } else if (!mutationFiredRef.current && !error && !conflictStatus) {
      mutationFiredRef.current = true
      if (isTrial) {
        selectPlanMutation.mutate({ planId, startTrial: true })
      }
      // Non-trial: show discount code input step instead of auto-firing
    }
  }, [planId, isAuthenticated, authLoading, isTrial, period, navigate, error, conflictStatus])

  const handleCreateOrder = () => {
    if (!planId) return
    createOrderMutation.mutate({
      planId,
      billingPeriod: period,
      discountCode: discountResult?.isValid ? discountCode.trim() : undefined
    })
  }

  const handleValidateDiscount = async () => {
    if (!discountCode.trim() || !planId) return
    setIsValidatingDiscount(true)
    setDiscountError(null)
    setDiscountResult(null)
    try {
      const response = await paymentsApi.validateDiscount({
        code: discountCode.trim(),
        planId,
        billingPeriod: period,
      })
      setDiscountResult(response.data)
      if (!response.data.isValid) {
        setDiscountError(response.data.errorMessage || 'Nieprawidłowy kod rabatowy.')
      }
    } catch {
      setDiscountError('Nie udało się zweryfikować kodu rabatowego.')
    } finally {
      setIsValidatingDiscount(false)
    }
  }

  const handlePay = () => {
    if (orderData?.paymentUrl) {
      window.location.href = orderData.paymentUrl
    }
  }


  // Conflict states (409 responses)
  if (conflictStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <img src="/assets/logo.png" alt="LexTool" className="h-12 mx-auto mb-4 object-contain" />
            {conflictStatus === 'already_on_trial' && (
              <>
                <AlertCircle className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Masz już aktywny okres próbny</h2>
                <p className="text-gray-600 mb-2">
                  Plan: <span className="font-medium">{PLAN_LABELS[trialData?.planName || ''] || PLAN_LABELS[planId || ''] || trialData?.planName || planId}</span>
                </p>
                {trialData?.trialEndsAt && (
                  <p className="text-gray-500 text-sm mb-6">
                    Okres próbny kończy się: {formatDateDDMMYYYY(trialData.trialEndsAt)}
                  </p>
                )}
                <button onClick={() => navigate('/', { replace: true })} className="btn-primary py-2 px-6">
                  Przejdź do panelu
                </button>
              </>
            )}
            {conflictStatus === 'already_active' && (
              <>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Masz już aktywną subskrypcję</h2>
                <p className="text-gray-600 mb-6">Twoja subskrypcja jest aktywna. Możesz korzystać z LexTool.</p>
                <button onClick={() => navigate('/', { replace: true })} className="btn-primary py-2 px-6">
                  Przejdź do panelu
                </button>
              </>
            )}
            {conflictStatus === 'trial_expired' && (
              <>
                <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Okres próbny został wykorzystany</h2>
                <p className="text-gray-600 mb-6">Wykup subskrypcję, aby kontynuować korzystanie z LexTool.</p>
                <button
                  onClick={() => {
                    setConflictStatus(null)
                    setTrialData(null)
                    setError(null)
                    mutationFiredRef.current = false
                    navigate(`/pricing?plan=${planId}&period=${period}`, { replace: true })
                  }}
                  className="btn-primary py-2 px-6 flex items-center justify-center gap-2 mx-auto"
                >
                  <CreditCard className="w-5 h-5" />
                  Wykup subskrypcję
                </button>
              </>
            )}
            {!['already_on_trial', 'already_active', 'trial_expired'].includes(conflictStatus) && (
              <>
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Wystąpił błąd</h2>
                <p className="text-gray-600 mb-6">Nie udało się aktywować okresu próbnego.</p>
                <button onClick={() => navigate('/', { replace: true })} className="btn-primary py-2 px-6">
                  Przejdź do panelu
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Trial success state
  if (trialData?.status === 'trial_started') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <img src="/assets/logo.png" alt="LexTool" className="h-12 mx-auto mb-4 object-contain" />
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Okres próbny aktywowany!</h2>
            <p className="text-gray-600 mb-1">
              Plan: <span className="font-medium">{PLAN_LABELS[trialData.planName || ''] || trialData.planName}</span>
            </p>
            {trialData.trialEndsAt && (
              <p className="text-gray-500 text-sm mb-6">
                Dostęp do: {formatDateDDMMYYYY(trialData.trialEndsAt)}
              </p>
            )}
            <button
              onClick={() => navigate('/', { replace: true })}
              className="btn-primary py-3 px-8 flex items-center justify-center gap-2 mx-auto"
            >
              <Sparkles className="w-5 h-5" />
              Przejdź do panelu
            </button>
            <p className="text-xs text-gray-400 mt-4">
              Okres próbny nie wymaga karty płatniczej.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-md w-full mx-4 text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <p className="text-red-600 mb-4">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setError(null)
                  if (isTrial) {
                    selectPlanMutation.mutate({ planId: planId!, startTrial: true })
                  } else {
                    setOrderData(null)
                    createOrderMutation.mutate({
                      planId: planId!,
                      billingPeriod: period,
                      discountCode: discountResult?.isValid ? discountCode : undefined
                    })
                  }
                }}
                className="btn-primary py-2 px-6"
              >
                Spróbuj ponownie
              </button>
              <div>
                <a
                  href={EXTERNAL_PRICING_URL}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Wróć do cennika
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Confirmation step: show proforma + pay button (payment flow only)
  if (orderData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <div className="max-w-lg w-full">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Wróć do panelu
          </button>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <img src="/assets/logo.png" alt="LexTool" className="h-12 mx-auto mb-4 object-contain" />
              <h1 className="text-2xl font-bold text-gray-900">
                Potwierdzenie zamówienia
              </h1>
            </div>

            {/* Order summary */}
            <div className="bg-gray-50 rounded-lg p-5 mb-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Plan:</span>
                <span className="font-medium text-gray-900">
                  {PLAN_LABELS[planId || ''] || planId}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Okres rozliczeniowy:</span>
                <span className="font-medium text-gray-900">
                  {period === 'yearly' ? 'Roczny' : 'Miesięczny'}
                </span>
              </div>
              {orderData.originalAmount != null && orderData.discountAmount != null && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Cena regularna:</span>
                    <span className="text-gray-400 line-through">
                      {orderData.originalAmount.toFixed(2)} {orderData.currency}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-1">
                      <Tag className="w-3.5 h-3.5 text-green-600" />
                      Rabat:
                    </span>
                    <span className="text-green-600 font-medium">
                      -{orderData.discountAmount.toFixed(2)} {orderData.currency}
                    </span>
                  </div>
                </>
              )}
              <div className="border-t border-gray-200 pt-3 flex justify-between">
                <span className="text-gray-700 font-medium">Do zapłaty:</span>
                <span className="text-xl font-bold text-gray-900">
                  {orderData.amount.toFixed(2)} {orderData.currency}
                  <span className="text-xs font-normal text-gray-500 ml-1">brutto</span>
                </span>
              </div>
            </div>

            {/* Proforma link */}
            {orderData.proformaUrl && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Faktura proforma
                      {orderData.proformaNumber && (
                        <span className="font-normal text-blue-700"> nr {orderData.proformaNumber}</span>
                      )}
                    </p>
                    <a
                      href={orderData.proformaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Pobierz / podgląd proformy
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Pay button or zero-price activation */}
            {orderData.amount === 0 || !orderData.paymentUrl ? (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-green-900">
                    Subskrypcja została aktywowana! Rabat pokrył całą kwotę — płatność nie jest wymagana.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/', { replace: true })}
                  className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
                >
                  Przejdź do panelu
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handlePay}
                  className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-5 h-5" />
                  Przejdź do płatności
                </button>

                <p className="text-xs text-gray-400 text-center mt-4">
                  Zostaniesz przekierowany do bezpiecznej bramki płatniczej Przelewy24.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Loading state for trial
  if (isTrial || !isAuthenticated || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">
            {isTrial ? 'Aktywowanie okresu próbnego...' : 'Ładowanie...'}
          </p>
        </div>
      </div>
    )
  }

  // Pre-order step: discount code input (non-trial payment flow)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-lg w-full">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Wróć do panelu
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <img src="/assets/logo.png" alt="LexTool" className="h-12 mx-auto mb-4 object-contain" />
            <h1 className="text-2xl font-bold text-gray-900">Zamówienie subskrypcji</h1>
          </div>

          <div className="bg-gray-50 rounded-lg p-5 mb-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Plan:</span>
              <span className="font-medium text-gray-900">{PLAN_LABELS[planId || ''] || planId}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Okres:</span>
              <span className="font-medium text-gray-900">{period === 'yearly' ? 'Roczny' : 'Miesięczny'}</span>
            </div>
          </div>

          {/* Discount code input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kod rabatowy (opcjonalnie)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={discountCode}
                onChange={(e) => {
                  setDiscountCode(e.target.value.toUpperCase())
                  setDiscountResult(null)
                  setDiscountError(null)
                }}
                placeholder="Wpisz kod rabatowy"
                className="input flex-1"
                maxLength={50}
              />
              <button
                onClick={handleValidateDiscount}
                disabled={!discountCode.trim() || isValidatingDiscount}
                className="btn-secondary px-4 py-2 text-sm whitespace-nowrap disabled:opacity-50"
              >
                {isValidatingDiscount ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Zastosuj'
                )}
              </button>
            </div>
            {discountError && (
              <p className="mt-1 text-sm text-red-600">{discountError}</p>
            )}
            {discountResult?.isValid && (
              <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <Tag className="w-4 h-4 text-green-600 flex-shrink-0" />
                <div className="text-sm">
                  <span className="font-medium text-green-800">{discountResult.description}</span>
                  {discountResult.originalPrice != null && discountResult.finalPrice != null && (
                    <span className="text-green-700 ml-2">
                      <span className="line-through text-gray-400">{discountResult.originalPrice.toFixed(2)} PLN</span>
                      {' → '}
                      <span className="font-bold">{discountResult.finalPrice.toFixed(2)} PLN</span>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleCreateOrder}
            disabled={createOrderMutation.isPending}
            className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
          >
            {createOrderMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Przygotowywanie zamówienia...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                Przejdź do płatności
              </>
            )}
          </button>

          <p className="text-xs text-gray-400 text-center mt-4">
            Zostaniesz przekierowany do bezpiecznej bramki płatniczej Przelewy24.
          </p>
        </div>
      </div>
    </div>
  )
}
